import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import { useSessionStorage } from 'usehooks-ts';
import { process, State } from '@progress/kendo-data-query';
import React, { useContext, useEffect, useState } from 'react';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import Toolbar from '../toolbar';
import {
  DeleteAreaAsync,
  GetResourceValue,
  UpdateGameDocState
} from '../../../utils/game-document';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import { LinkCell } from '../../../components/grid';
import { ColumnMenu } from '../../../components/columnMenu';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { NoRecords } from '../../../components/grid/no-records';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { GameDocumentContext } from '../../../contexts/game-document';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import ViewEditDeleteCell from '../../../components/grid/view-edit-delete-cell';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const Areas = () => {
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-areas',
    initialDataState
  );
  const [showConfirmationDelete, setShowConfirmationDelete] = useState('');

  const navigate = useNavigate();

  const areaData = React.useMemo(() => {
    if (!state.gameDocument) return [];
    return (state.gameDocument.assets.areas ?? []).map((area) => ({
      ...area,
      title: GetResourceValue(state.gameDocument!, area.titleResId ?? '')
    }));
  }, [state.gameDocument]);

  useEffect(() => {
    // Set the page zone.
    document.title = `Areas - ${state.gameDocument?.name}`;
  }, [state]);

  const LinkedTitleCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props}></LinkCell>
  );

  const onViewEntity = (entityId: string) => {
    navigate(`../../map?areaId=${entityId}`);
  };

  const DeleteCell = (props: GridCellProps) => (
    <ViewEditDeleteCell
      onViewClick={() => onViewEntity(props.dataItem['id'])}
      onEditClick={() => navigate(`${props.dataItem['id']}`)}
      onDeleteClick={() => {
        setShowConfirmationDelete(props.dataItem['id']);
      }}
      {...props}
    />
  );

  const onDeleteEntity = async (entityId: string) => {
    DeleteAreaAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        setShowConfirmationDelete('');
      }
    );
  };

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/areas/*', text: 'Areas' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }
    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Areas'}
        buttonHelpSupport={{
          title: 'Area assets guide',
          url: 'https://forum.catalystglobal.com/t/4589'
        }}></Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search areas'}
          columnsToSearch={['title', 'description']}
          showCardMode={false}
          exportRef={gridExportRef}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
        />
        <ExcelExport
          data={areaData}
          ref={gridExportRef}
          fileName={`${state.gameDocument?.name} areas.xlsx`}>
          <Grid
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={{ info: true, pageSizes: [20, 50, 100] }}
            pageSize={20}
            sortable={true}
            className={'cg-grid3'}
            data={process(areaData, dataState)}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'title'}
              title={'Title'}
              cells={{ data: LinkedTitleCell }}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'description'}
              title={'Description'}
              columnMenu={ColumnMenu}
            />
            <Column cells={{ data: DeleteCell }} />
            <GridNoRecords>
              <NoRecords />
            </GridNoRecords>
          </Grid>
        </ExcelExport>
      </div>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(showConfirmationDelete)
              : setShowConfirmationDelete('')
          }
          onClose={() => setShowConfirmationDelete('')}>
          Are you sure you want to remove the area?
        </YesNoDialog>
      )}
    </>
  );
};

export default Areas;
